import React, { useState, useEffect } from "react";
import { api, errMsg, toPaise, fmtDate, fmtDateTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData, useStoreDayLock } from "@/hooks/useAsyncData";
import { Money, MoneyInput, StoreDatePicker, DayLockBanner, LoadErrorState, LoadingState, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calculator, AlertTriangle, SendHorizontal } from "lucide-react";
import { toast } from "sonner";

const Line = ({ label, paise, neg, strong, testId, hint }) => (
  <div className={`flex justify-between text-sm ${strong ? "font-semibold border-t pt-1.5 mt-1" : ""}`} data-testid={testId}>
    <span className={neg ? "text-muted-foreground" : ""}>
      {label}
      {hint && <span className="block text-[10px] text-muted-foreground font-normal">{hint}</span>}
    </span>
    <Money paise={neg ? -paise : paise} signed={neg} colored={neg && paise > 0} />
  </div>
);

const CashierCollections = ({ rows, userId }) => (
  <Card className="rounded-lg overflow-hidden">
    <div className="px-4 py-2.5 border-b"><p className="font-display font-semibold text-sm" data-testid="cashier-collections-heading">Cash collected by cashier — all goes into the store drawer</p></div>
    <div className="overflow-x-auto">
      <Table className="table-compact">
        <TableHeader><TableRow>
          <TableHead>Cashier</TableHead>
          <TableHead className="text-right">Bills</TableHead>
          <TableHead className="text-right">Cash In</TableHead>
          <TableHead className="text-right">Non-cash</TableHead>
          <TableHead className="text-right">Cash expenses</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center py-4">No collections yet</TableCell></TableRow>}
          {rows.map((c) => (
            <TableRow key={c.cashier_id} data-testid={`collection-row-${c.cashier_name?.replace(/\s+/g, "-").toLowerCase()}`}>
              <TableCell className="font-medium">{c.cashier_name}{c.cashier_id === userId && <span className="text-[11px] text-[hsl(var(--brass))] ml-1">(you)</span>}</TableCell>
              <TableCell className="amount-cell">{c.bill_count}</TableCell>
              <TableCell className="amount-cell"><Money paise={c.cash_from_bills_paise - c.cash_excess_returned_paise + c.adj_cash_receipts_paise - c.adj_cash_deductions_paise} /></TableCell>
              <TableCell className="amount-cell"><Money paise={c.noncash_from_bills_paise} /></TableCell>
              <TableCell className="amount-cell"><Money paise={c.cash_expenses_paise} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </Card>
);

export default function CashCountPage() {
  const { user, today, storeId, date } = useApp();
  const isAdmin = user.role === "admin";
  const effStore = isAdmin ? storeId : user.store_id;
  const effDate = isAdmin ? date : today;
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState(false);

  const { storeDay, locked, refresh: refreshLock } = useStoreDayLock(effStore, effDate);

  const q = useAsyncData(
    () => {
      if (!effStore || !effDate) return Promise.resolve(null);
      return api.get("/cash-counts/expected", { params: { store_id: effStore, business_date: effDate } }).then((r) => r.data);
    },
    [effStore, effDate]
  );

  useEffect(() => {
    const c = q.data?.count;
    if (c) { setCounted(String(c.counted_paise / 100)); setNote(c.note || ""); }
    else { setCounted(""); setNote(""); }
    setCorrecting(false);
  }, [q.data]);

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <h1 className="font-display text-xl font-semibold arch-underline">Store closing cash count</h1>
        <p className="text-sm text-muted-foreground">Count the physical cash in the drawer <b>before</b> sending any of it to HQ — one count per store per day</p>
      </div>
      {isAdmin && <StoreDatePicker />}
    </div>
  );

  if (q.error) return <div className="space-y-5">{header}<LoadErrorState error={q.error} onRetry={q.reload} title="Could not load cash count data" /></div>;
  if (q.loading) return <div className="space-y-5">{header}<LoadingState /></div>;
  if (!q.data) return <div className="space-y-5">{header}<EmptyState icon={Calculator} title="Select a store and date" sub="Nothing to show for the current selection" /></div>;

  const { summary: s, count, cashiers, state, hq_sends: sends = [] } = q.data;
  const countedP = toPaise(counted);
  const variance = counted === "" ? null : countedP - s.expected_pre_hq_paise;
  const stale = !!state?.stale;
  const showForm = !count || correcting || !stale;

  const submit = async () => {
    if (locked) { toast.error("This business date is finalized and locked"); return; }
    if (variance !== 0 && variance !== null && !note.trim()) {
      toast.error("Variance detected — a reason/note is compulsory");
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/cash-counts", { store_id: effStore, business_date: effDate, counted_paise: countedP, note: note || null });
      toast.success(`Count recorded — variance ${res.variance_paise === 0 ? "nil" : (res.variance_paise / 100).toFixed(2)}`);
      q.refresh(); refreshLock();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const confirmSame = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/cash-counts/confirm", { store_id: effStore, business_date: effDate, note: note || null });
      toast.success(`Count confirmed — variance ${res.variance_paise === 0 ? "nil" : (res.variance_paise / 100).toFixed(2)}`);
      q.refresh(); refreshLock();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {header}

      <DayLockBanner storeDay={storeDay} />

      {s.carry_blocked && (
        <div className="rounded-lg border border-[hsl(var(--danger))]/45 bg-[hsl(var(--danger))]/8 px-4 py-3 text-sm flex items-start gap-2" data-testid="carry-blocked-banner" role="alert">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))] mt-0.5 shrink-0" />
          <p>
            <b className="text-[hsl(var(--danger))]">Opening cannot be carried forward yet.</b>{" "}
            {(s.missing_count_dates || []).map((d) => fmtDate(d)).join(", ")} had cash activity but no closing cash count,
            so nothing is carried silently across {(s.missing_count_dates || []).length > 1 ? "those days" : "that day"}.
            Record the missing count first — today's entries can continue, but this day cannot be finalized until then.
          </p>
        </div>
      )}

      {s.opening_provisional && (
        <div className="rounded-lg border border-[hsl(var(--warning))]/45 bg-[hsl(var(--warning))]/10 px-4 py-2.5 text-sm flex items-start gap-2" data-testid="provisional-carry-banner" role="status">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
          <p>
            Opening is <b>provisional</b> — carried from the count of {fmtDate(s.opening_source_date)}, which is not finalized yet.
            You can keep recording today's cash; this day can be finalized once {fmtDate(s.opening_source_date)} is finalized.
          </p>
        </div>
      )}

      {stale && !locked && (
        <div className="rounded-lg border border-[hsl(var(--danger))]/45 bg-[hsl(var(--danger))]/8 px-4 py-3 space-y-2.5" data-testid="count-stale-banner" role="alert">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))] mt-0.5 shrink-0" />
            <div className="text-sm min-w-0">
              <p className="font-semibold text-[hsl(var(--danger))]">The day's entries changed after this count was taken</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Counted <Money paise={count.counted_paise} className="text-foreground" /> when{" "}
                <Money paise={state.submitted_expected_paise} className="text-foreground" /> was expected.
                The ledger now expects <Money paise={state.current_expected_pre_hq_paise} className="text-foreground" /> —
                variance would be <Money paise={state.current_variance_paise} signed colored />.
                Finalization stays blocked until this is confirmed or corrected.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={confirmSame} disabled={busy}
              className="bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="count-confirm-button">
              Confirm same count <Money paise={count.counted_paise} className="ml-1" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCorrecting(true)} data-testid="count-correct-button">
              Correct the count
            </Button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <Card className="rounded-lg">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Expected cash before any HQ send — {fmtDate(effDate)}</p>
            <Line label="Opening cash carried" paise={s.opening_carry_paise}
              hint={s.opening_source_date ? `from the count of ${fmtDate(s.opening_source_date)}${s.carry?.hq_sent_paise ? `, less ${(s.carry.hq_sent_paise / 100).toLocaleString("en-IN")} sent to HQ` : ""}` : "first day for this store"}
              testId="line-opening-carry" />
            {!!s.opening_adjustment_paise && (
              <Line label="Admin opening adjustment (audited)" paise={s.opening_adjustment_paise} testId="line-opening-adjustment" />
            )}
            <Line label="Effective opening" paise={s.opening_paise} testId="line-opening" />
            <Line label={`Cash received on bills (${s.bill_count} bills, all cashiers)`} paise={s.cash_from_bills_paise} testId="line-cash-bills" />
            {s.excess_kept_paise > 0 && (
              <div className="flex justify-between text-sm text-[hsl(var(--success))]" data-testid="extra-kept-line">
                <span className="pl-3">includes extra received (kept)</span>
                <Money paise={s.excess_kept_paise} signed />
              </div>
            )}
            <Line label="Excess returned in cash" paise={s.cash_excess_returned_paise} neg />
            <Line label="Other cash receipts" paise={s.adj_cash_receipts_paise} />
            <Line label="Cash deductions" paise={s.adj_cash_deductions_paise} neg />
            <Line label="Cash expenses / business payments" paise={s.cash_expenses_paise} neg />
            <Line label="Expected cash in drawer (pre-HQ)" paise={s.expected_pre_hq_paise} strong testId="line-expected" />
            <p className="text-[11px] text-muted-foreground mt-2">
              Cash sent to HQ is <b>never</b> subtracted here — count the drawer first, then send. Less Taken is reported separately and never subtracted from cash.
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardContent className="p-4 space-y-3">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Actual physical cash (before HQ send)</p>
            <MoneyInput value={counted} onChange={setCounted} className="h-12 text-lg" testId="cash-count-input" disabled={locked || (stale && !correcting)} />
            {variance !== null && (
              <div className={`rounded p-2.5 text-sm font-semibold ${variance === 0 ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]"}`} data-testid="variance-preview">
                {variance === 0 ? "Balanced — no variance" : variance < 0
                  ? <>Shortage <Money paise={variance} signed /></>
                  : <>Excess <Money paise={variance} signed /></>}
              </div>
            )}
            {variance !== null && variance !== 0 && !locked && (
              <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Reason / note (compulsory for variance)" data-testid="cash-count-note-input" />
            )}
            <Button onClick={submit} disabled={busy || counted === "" || locked || (stale && !correcting)}
              className="w-full h-11 bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="cash-count-submit-button">
              {locked ? "Day locked" : count ? (correcting ? "Save corrected count" : "Resubmit count") : "Submit count"}
            </Button>
            {count && (
              <div className="space-y-1.5">
                <p className="text-[11px] text-muted-foreground" data-testid="count-submitted-info">
                  Counted <Money paise={count.counted_paise} /> (variance <Money paise={state.current_variance_paise} signed />) by {count.submitted_by_name} · {fmtDateTime(count.submitted_at)}
                  {count.last_action === "reconfirm" && count.confirmed_by_name && <> · re-confirmed by {count.confirmed_by_name}</>}
                  {count.history?.length > 0 && <> · {count.history.length} earlier record{count.history.length > 1 ? "s" : ""} kept in history</>}
                </p>
                <div className="rounded border border-[hsl(var(--border)/0.5)] p-2.5 text-[11px] space-y-1" data-testid="post-count-hq">
                  <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><SendHorizontal className="h-3 w-3" />Sent to HQ from this close</span><Money paise={state.hq_sent_from_close_paise} /></div>
                  <div className="flex justify-between font-semibold"><span>Stays for the next day's opening</span><Money paise={state.next_opening_paise} /></div>
                  {sends.map((t) => (
                    <div key={t.id} className="flex justify-between text-muted-foreground">
                      <span>· {t.by} on {fmtDate(t.entered_on)} {t.note ? `— ${t.note}` : ""}</span>
                      <Money paise={-t.amount_paise} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CashierCollections rows={cashiers} userId={user.id} />
    </div>
  );
}
